import { useEffect, useState, useCallback } from 'react';
import { PartyPopper } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { QuarterRecapCard } from './QuarterRecapCard';
import { hasConsentChoice } from './CookieBanner';

/**
 * QuarterRecapPrompt — 2026-11-01 (owner: "pop-up the next time they open the
 * app"). Shows the latest closed Global Quarterly result ONCE per player
 * (seen-state lives on the server, so once across devices). The same card
 * stays on that quarter's Final standings afterwards.
 */
export default function QuarterRecapPrompt({ onViewStandings }) {
  const { user } = useAuth();
  const [recap, setRecap] = useState(null);
  // Wait for the cookie choice: its banner sits above dialogs and would cover the buttons.
  const [consented, setConsented] = useState(hasConsentChoice);

  useEffect(() => {
    const onConsent = () => setConsented(true);
    window.addEventListener('fws:consent-changed', onConsent);
    return () => window.removeEventListener('fws:consent-changed', onConsent);
  }, []);

  useEffect(() => {
    if (!user?.id) return undefined;
    let alive = true;
    api.get('/recaps/quarter/pending')
      .then((res) => {
        if (!alive || !res?.data?.recap) return;
        setRecap(res.data.recap);
        if (hasConsentChoice()) setConsented(true);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [user?.id]);

  const close = useCallback(() => {
    setRecap((r) => {
      if (r) api.post(`/recaps/quarter/${r.league_id}/seen`).catch(() => {});
      return null;
    });
  }, []);

  const view = () => {
    const leagueId = recap?.league_id;
    close();
    if (leagueId) onViewStandings?.(leagueId);
  };

  return (
    <Dialog open={!!recap && consented} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="sm:max-w-sm" data-testid="quarter-recap-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PartyPopper className="h-4 w-4 text-[hsl(var(--accent))]" />
            {recap ? `${recap.quarter_label} is over — here's how you finished` : ''}
          </DialogTitle>
          <DialogDescription>
            The quarter's teams are frozen as final standings. Share your result!
          </DialogDescription>
        </DialogHeader>
        <QuarterRecapCard recap={recap} testid="quarter-recap-prompt-card" />
        <div className="flex flex-col gap-2">
          <Button onClick={view} data-testid="quarter-recap-view-standings-btn">View final standings</Button>
          <Button variant="ghost" onClick={close} data-testid="quarter-recap-close-btn">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
